import { ContentBlockType } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetSpace } from '@onesource/services';

import { useContentRoute } from '../../hooks/use-content-route';

/**
 * Shared resolver for the slug-based item viewers.
 *
 * Module-level viewers (`/programs/{program}/{module}/{lesson}/{type}/{itemSlug}`)
 * read the lesson id from the lesson layout's content-route context. Single-lesson
 * program viewers (`/programs/{program}/{type}/{itemSlug}`) have no module/lesson
 * in context — there the space IS its own module and its only lesson is the
 * target, so we resolve that from the program id. Either way, the block is found
 * by url_slug within the lesson (no extra resolver round-trip).
 */
export const useResolvedBlock = (itemSlug: string, blockType: ContentBlockType) => {
  const {
    programId,
    programSlug,
    subSpaceId = '',
    subSpaceSlug = '',
    lessonId: ctxLessonId = '',
    lessonSlug = '',
  } = useContentRoute();

  // No sub-space in context => single-lesson program: the space is its own module.
  const isSingleLesson = !subSpaceId;
  const moduleId = isSingleLesson ? programId : subSpaceId;

  const { data: lessons } = useGetLessons(moduleId);
  const lessonId = isSingleLesson ? (lessons?.[0]?.id ?? '') : ctxLessonId;

  const { data: lesson, isLoading: isLoadingLesson, error } = useGetLesson(lessonId, { enabled: !!lessonId });
  const { data: module } = useGetSpace(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId) ?? null;

  // A replayed live session is served as video content, so the video viewer has to
  // match it even though the row is still an EVENT block.
  const matchesType = (b: { blockType: ContentBlockType; isReplay?: boolean }) =>
    b.blockType === blockType ||
    (blockType === ContentBlockType.VIMEO && b.blockType === ContentBlockType.EVENT && b.isReplay === true);

  const block = lesson?.contentBlocks?.find(b => b.urlSlug === itemSlug && matchesType(b)) ?? null;

  // Base URL of the owning lesson — /programs/{program} for single-lesson, or the
  // full /programs/{program}/{module}/{lesson} chain otherwise. Viewers use it to
  // navigate to sibling pages (quiz -> results, results -> quiz, back to lesson).
  const lessonBasePath = isSingleLesson
    ? `/programs/${programSlug}`
    : `/programs/${programSlug}/${subSpaceSlug}/${lessonSlug}`;

  return {
    // ids
    programId,
    moduleId,
    lessonId,
    contentId: block?.id ?? '',
    // slugs / paths (for breadcrumbs / navigation)
    programSlug,
    moduleSlug: subSpaceSlug,
    lessonSlug,
    lessonBasePath,
    isSingleLesson,
    // data
    lesson,
    module,
    lessons,
    findParentLesson,
    block,
    isLoading: isLoadingLesson || (isSingleLesson && lessons === undefined),
    error,
  };
};
